import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck,
  Search,
  FileCheck2,
  Users2,
  Award,
  GraduationCap,
  ArrowRight,
  CheckCircle2,
  Scale,
} from "lucide-react";
import { ORG_LOGOS, INSTITUTION_IMG } from "@/lib/api";
import Seal from "@/components/Seal";

const stats = [
  { label: "Active Credentials", value: "2,400+" },
  { label: "Independent Reviewers", value: "180+" },
  { label: "Jurisdictions Recognized", value: "32" },
  { label: "Disciplinary Resolutions Published", value: "100%" },
];

const principles = [
  {
    icon: Scale,
    title: "Independent Review",
    body: "Every application is evaluated by credentialing committees outside the applicant's referral network.",
  },
  {
    icon: ShieldCheck,
    title: "Public Accountability",
    body: "All issued credentials are searchable in our public registry, including status, scope, and standing.",
  },
  {
    icon: Award,
    title: "Continuing Education",
    body: "Active holders must demonstrate ongoing competency through tracked CE hours and re-attestations.",
  },
  {
    icon: GraduationCap,
    title: "Education Verification",
    body: "Original transcripts, supervised practice, and clinical hours are verified before approval.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white/95 to-slate-50" />
        <div className="absolute inset-0 bg-grid-navy opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 shadow-sm text-xs font-medium text-slate-700 mb-6">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                Recognized Accreditation Bodies · Independently Operated
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary tracking-tight leading-[1.05]">
                Accrediting institutions. Certifying professionals. Protecting the public.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                <strong className="text-primary">ACIS</strong> accredits institutions and educational
                programs in integrative health sciences. <strong className="text-primary">IBTHM</strong>
                {" "}board-certifies and licenses individual practitioners. Together they provide a
                public, independently verifiable trust framework for students, employers, regulators,
                and patients.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button data-testid="hero-apply-btn" size="lg" className="bg-primary text-white hover:bg-[#113A64] h-12 px-6">
                    Apply for credentials
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/registry">
                  <Button data-testid="hero-registry-btn" size="lg" variant="outline" className="h-12 px-6 border-slate-300">
                    <Search className="mr-2 h-4 w-4" />
                    Search the public registry
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Public registry</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Disciplinary transparency</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> CE tracking &amp; renewals</span>
              </div>
              <div className="mt-8 flex items-center gap-6" data-testid="hero-seals">
                <Seal kind="acis_accredited_member" size="md" showLabel />
                <Seal kind="ibthm_board_certified" size="md" showLabel />
                <Seal kind="ibthm_licensed" size="md" showLabel />
              </div>
            </div>

            <div className="lg:col-span-5 animate-fade-up">
              <div className="relative">
                <div className="rounded-lg border border-slate-200 bg-white shadow-[0_20px_60px_-25px_rgba(10,37,64,0.25)] p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Two boards. One system.</div>
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">
                      <ShieldCheck className="h-3 w-3 mr-1" /> Verified
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { logo: ORG_LOGOS.ACIS, name: "ACIS", full: "Accreditation Commission for Integrative Sciences" },
                      { logo: ORG_LOGOS.IBTHM, name: "IBTHM", full: "International Board of Trichology & Holistic Medicine" },
                    ].map((o) => (
                      <div key={o.name} className="rounded-md border border-slate-200 bg-slate-50 p-4">
                        <img src={o.logo} alt={o.name} className="h-12 w-12 object-contain mb-3" />
                        <div className="font-heading font-bold text-primary text-sm">{o.name}</div>
                        <div className="text-xs text-slate-600 leading-snug mt-1">{o.full}</div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-200 mt-5 pt-5 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Last credential issued</div>
                      <div className="text-sm font-mono-data text-primary mt-1">ACIS-2025-01184</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Avg. review time</div>
                      <div className="text-sm font-mono-data text-primary mt-1">21 days</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-3 -right-3 -z-10 h-full w-full rounded-lg border border-slate-200 bg-slate-100" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-heading text-3xl lg:text-4xl font-bold text-primary tracking-tight">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">Our standards</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-primary tracking-tight">
              A credential issued here means something specific.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              We operate under public-interest standards rather than membership-revenue incentives. The
              same review pathway applies to every applicant.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {principles.map((p) => (
              <Card key={p.title} className="p-6 border-slate-200 bg-white shadow-sm hover:shadow-md transition-all">
                <div className="h-10 w-10 rounded-md bg-primary/5 text-primary flex items-center justify-center mb-4">
                  <p.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="font-heading font-semibold text-primary">{p.title}</div>
                <div className="text-sm text-slate-600 mt-2 leading-relaxed">{p.body}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TWO BOARDS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <img
              src={INSTITUTION_IMG}
              alt="Institution"
              className="rounded-lg border border-slate-200 shadow-sm object-cover w-full h-[420px]"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">About the boards</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-primary tracking-tight">
              Two specialized boards under one accountability framework.
            </h2>
            <div className="mt-8 space-y-6">
              <div className="rounded-lg border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <img src={ORG_LOGOS.ACIS} alt="ACIS" className="h-10 w-10 object-contain" />
                  <div>
                    <div className="font-heading font-bold text-primary">ACIS</div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">Accreditation Commission for Integrative Sciences</div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Credentials practitioners in integrative health sciences, clinical nutrition,
                  functional medicine support, and allied integrative disciplines.
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <img src={ORG_LOGOS.IBTHM} alt="IBTHM" className="h-10 w-10 object-contain" />
                  <div>
                    <div className="font-heading font-bold text-primary">IBTHM</div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">International Board of Trichology &amp; Holistic Medicine</div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Credentials Certified Trichologists, Holistic Medicine Practitioners, and scalp-and-hair
                  health specialists with documented clinical training.
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about">
                <Button variant="outline" className="border-slate-300" data-testid="learn-more-btn">Read full mandate</Button>
              </Link>
              <Link to="/standards">
                <Button variant="ghost" className="text-primary">View standards →</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-4">For practitioners</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Begin your credentialing pathway.</h2>
            <p className="mt-4 text-white/80 max-w-xl">
              Submit your application, upload supporting documentation, and track every stage in your
              applicant portal — from review to credential issuance.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button data-testid="cta-apply-btn" className="bg-[#C5A059] text-primary hover:bg-[#D4AF37] h-12 px-6 font-semibold">
                  Start an application
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 h-12 px-6">
                  I already have an account
                </Button>
              </Link>
            </div>
          </div>
          <div className="rounded-lg border border-white/15 bg-white/[0.04] p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-4">For the public</div>
            <h3 className="font-heading text-xl font-semibold">Verify a credential in seconds.</h3>
            <p className="text-sm text-white/70 mt-2 leading-relaxed">
              Enter a credential number or practitioner name to confirm active status, scope of practice, and disciplinary history.
            </p>
            <Link to="/verify">
              <Button data-testid="cta-verify-btn" className="mt-5 bg-white text-primary hover:bg-slate-100">
                <FileCheck2 className="h-4 w-4 mr-2" />
                Verify a credential
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
